import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, map, switchMap, tap, take, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';

import { AppState } from '../app.state';
import { selectIsAuthenticated, selectUserUuid } from '../user/user.selectors';
import { OrderService } from '../../services/order.service';
import { ToastService } from '../../../shared/services/toast.service';
import { I18nService } from '../../services/i18n.service';
import { ChannelEventsService, CartEventItem } from '../../../core/services/channel-events.service';
import { AvailableOffersFacade } from '../available-offers/available-offers.facade';
import { isCartHydrationBlocked } from '../../utils/cart-hydration.util';
import { shouldSkipCampaignCartHydration } from '../../campaign/campaign-cart-reset';

import { CartValidationService } from './cart-validation.service';
import { CartCacheService } from './cart-cache.service';
import { CartErrorHandlerService } from './cart-error-handler.service';
import { CartSyncService } from './cart-sync.service';
import { CartPersistenceService } from './cart-persistence.service';
import { selectCartItems } from './cart.selectors';
import { createCartItem, CartItem } from './cart.state';
import { 
  addItem, 
  removeItem, 
  updateQuantity, 
  updateProduct, 
  clearCart, 
  loadCart, 
  loadCartSuccess, 
  loadCartFromDraft,
  syncCart, 
  operationSuccess,
  operationFailure,
  setLoading
} from './cart.actions';


@Injectable()
export class CartEffects {
  private actions$ = inject(Actions);
  private store = inject(Store<AppState>);
  private orderService = inject(OrderService);
  private validationService = inject(CartValidationService);
  private cacheService = inject(CartCacheService);
  private errorHandler = inject(CartErrorHandlerService);
  private syncService = inject(CartSyncService);
  private persistenceService = inject(CartPersistenceService);
  private toastService = inject(ToastService);
  private channelEventsService = inject(ChannelEventsService);
  private i18nService = inject(I18nService);
  private availableOffersFacade = inject(AvailableOffersFacade);
  
  private convertCartItemsToCartEventItems(cartItems: CartItem[]): CartEventItem[] {
    return (cartItems ?? []).map(item => {
      const variation = item.selectedVariation;

      const amount =
        variation?.salesPrice ??
        variation?.price ??
        item.product?.salesPrice ??
        item.product?.price ??
        0;

      return {
        productName: item.product?.name || null,
        variationName: variation?.name ?? null,
        variationUuid: variation?.uuid ?? null,
        quantity: item.quantity,
        amount,
        isCartEmpty: false,
      };
    });
  }

  private sendAddedCartEventWithAllItems(cartItems: CartItem[]): void {
    const cartEventItems = this.convertCartItemsToCartEventItems(cartItems);
    this.channelEventsService.sendAddedCartEvent(cartEventItems).subscribe({
      next: () => {
        // Fire-and-forget analytics event.
      },
      error: (err) => console.error('Error sending PRODUCT_ADDED_CART event:', err)
    });
  }

  private sendProductRemovedCartEvent(
    removedItems: CartItem[],
    remainingItems: CartItem[],
    isCartEmpty: boolean
  ): void {
    const products = isCartEmpty
      ? [
          {
            productName: null,
            variationName: null,
            variationUuid: null,
            quantity: 0,
            amount: 0,
            isCartEmpty: true,
          },
        ]
      : this.convertCartItemsToCartEventItems(removedItems);

    this.channelEventsService
      .sendProductRemovedCartEvent({
        products,
        isCartEmpty,
        remainingProducts: this.convertCartItemsToCartEventItems(remainingItems),
      })
      .subscribe({
        next: () => {
          // Fire-and-forget analytics event.
        },
        error: (err) => console.error('Error sending PRODUCT_REMOVED_CART event:', err),
      });
  }

  addItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addItem),
      tap(({ product, quantity, selectedVariation, suppressSuccessToast }) => {
        const validationResult = this.validationService.validateProductForCart(
          product, 
          quantity, 
          selectedVariation
        );
        
        if (!validationResult.isValid) {
          const error = this.errorHandler.handleValidationError(
            validationResult.errors.join(', '),
            'Add Item'
          );
          this.store.dispatch(operationFailure({ 
            error: error.message, 
            context: 'addItem' 
          }));
          return;
        }
        
        this.cacheService.cacheProduct(product);
        if (selectedVariation) {
          this.cacheService.cacheVariation(selectedVariation);
        }

        // Sends PRODUCT_ADDED_CART event with all cart items after adding
        this.store.select(selectCartItems).pipe(take(1)).subscribe(items => {
          // Simulates the item being added to have the complete state
          const newItem = createCartItem(product, quantity, selectedVariation);
          const existingItem = items.find(i => i.id === newItem.id);
          let updatedItems: CartItem[];
          
          if (existingItem) {
            updatedItems = items.map(i => 
              i.id === newItem.id 
                ? { ...i, quantity: i.quantity + quantity }
                : i
            );
          } else {
            updatedItems = [...items, newItem];
          }
          
          this.sendAddedCartEventWithAllItems(updatedItems);
        });

        if (!suppressSuccessToast) {
          this.toastService.showSuccess(
            this.i18nService.instant('components.cart.messages.addSuccess')
          );
        }
      }),
      map(() => operationSuccess())
    )
  );

  removeItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeItem),
      switchMap(({ itemId, removedItem }) =>
        this.store.select(selectCartItems).pipe(
          take(1),
          tap((remainingItems) => {
            if (removedItem) {
              this.sendProductRemovedCartEvent(
                [removedItem],
                remainingItems,
                remainingItems.length === 0
              );
            }

            this.cacheService.removeCachedCartItem(itemId);
          }),
          map(() => operationSuccess())
        )
      )
    )
  );

  updateQuantity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateQuantity),
      switchMap(({ itemId, quantity }) => 
        this.store.select(selectCartItems).pipe(
          take(1),
          tap((items) => {
            const item = items.find(i => i.id === itemId);
            if (!item) {
              const error = this.errorHandler.handleValidationError(
                'Item não encontrado no carrinho',
                'Update Quantity'
              );
              this.store.dispatch(operationFailure({ 
                error: error.message, 
                context: 'updateQuantity' 
              }));
              return;
            }
            
            if (quantity <= 0) {
              const error = this.errorHandler.handleValidationError(
                'Quantidade deve ser maior que zero',
                'Update Quantity'
              );
              this.store.dispatch(operationFailure({ 
                error: error.message, 
                context: 'updateQuantity' 
              }));
              return;
            }
            
            const updatedItems = items.map(i => 
              i.id === itemId 
                ? { ...i, quantity, lastModified: Date.now() }
                : i
            );
            
            this.sendAddedCartEventWithAllItems(updatedItems);
            
            const updatedItem = { ...item, quantity, lastModified: Date.now() };
            this.cacheService.cacheCartItem(updatedItem);
          }),
          map(() => operationSuccess())
        )
      )
    )
  );

  updateProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProduct),
      tap(({ itemId: _itemId, product }) => {
        const validationResult = this.validationService.validateProductForCart(
          product, 
          1
        );
        
        if (!validationResult.isValid) {
          const error = this.errorHandler.handleValidationError(
            validationResult.errors.join(', '),
            'Update Product'
          );
          this.store.dispatch(operationFailure({ 
            error: error.message, 
            context: 'updateProduct' 
          }));
          return;
        }
        
        this.cacheService.cacheProduct(product);
      }),
      map(() => operationSuccess())
    )
  );

  clearCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clearCart),
      tap(({ clearedItems, emitEvent }) => {
        // Só emite PRODUCT_REMOVED_CART no clear explícito do usuário
        if (emitEvent) {
          this.sendProductRemovedCartEvent(clearedItems ?? [], [], true);
        }

        this.cacheService.clearAllCaches();
        this.syncService.clearCartFromBackend();
      }),
      map(() => operationSuccess())
    )
  );

  loadCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCart),
      tap(() => this.store.dispatch(setLoading({ loading: true }))),
      switchMap(() => 
        this.store.select(selectIsAuthenticated).pipe(
          switchMap(isAuthenticated => {
            if (isAuthenticated) {
              return this.loadFromBackend();
            } else {
              return this.loadFromLocalStorage();
            }
          })
        )
      ),
      map(({ items, source }) => {
        const validationResult = this.validationService.validateCartItems(items);
        const validItems = this.validationService.sanitizeCartItems(items);
        
        this.cacheService.cacheCartItems(validItems);
        
        if (validationResult.warnings.length > 0) {
          console.warn('Cart validation warnings:', validationResult.warnings);
        }
        
        return loadCartSuccess({ items: validItems, source });
      }),
      catchError(error => {
        const cartError = this.errorHandler.handleError(error, 'Load Cart');
        return of(operationFailure({ 
          error: cartError.message, 
          context: 'loadCart' 
        }));
      })
    )
  );

  loadCartFromDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCartFromDraft),
      map(({ draft }) => {
        const cartItems: CartItem[] = (draft?.orderItems ?? [])
          .filter(item => Boolean(item?.product?.uuid))
          .map(item => createCartItem(item.product, item.quantity, item.selectedVariation));

        return { items: cartItems, source: 'backend' as const };
      }),
      map(({ items, source }) => {
        const validationResult = this.validationService.validateCartItems(items);
        const validItems = this.validationService.sanitizeCartItems(items);

        this.cacheService.cacheCartItems(validItems);

        if (validationResult.warnings.length > 0) {
          console.warn('Cart validation warnings:', validationResult.warnings);
        }

        // Atualiza o mapeamento de offers->variacoes para produtos do carrinho.
        const productsByKey = new Map<string, any>();
        validItems.forEach(i => {
          const key = i.product?.uuid || String(i.product?.id || '');
          if (key && !productsByKey.has(key)) {
            productsByKey.set(key, i.product);
          }
        });
        productsByKey.forEach(product => this.availableOffersFacade.mapProductVariationsToOffers(product));

        return loadCartSuccess({ items: validItems, source });
      }),
      catchError(error => {
        const cartError = this.errorHandler.handleError(error, 'Load Cart From Draft');
        return of(operationFailure({
          error: cartError.message,
          context: 'loadCartFromDraft'
        }));
      })
    )
  );

  syncCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncCart),
      debounceTime(1000),
      switchMap(() => 
        this.store.select(selectIsAuthenticated).pipe(
          switchMap(isAuthenticated => {
            if (isAuthenticated) {
              return this.syncToBackend();
            } else {
              return this.syncToLocalStorage();
            }
          })
        )
      ),
      map(() => operationSuccess()),
      catchError(error => {
        const cartError = this.errorHandler.handleSyncError(error, 'Sync Cart');
        return of(operationFailure({ 
          error: cartError.message, 
          context: 'syncCart' 
        }));
      })
    )
  );

  autoSyncCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addItem, removeItem, updateQuantity, updateProduct),
      withLatestFrom(this.store.select(selectIsAuthenticated)),
      debounceTime(500),
      tap(([_action, isAuthenticated]) => {
        if (isAuthenticated) {
          this.syncService.syncCartToBackend();
        } else {
          this.persistenceService.saveCartToLocalStorage();
        }
      }),
      map(() => operationSuccess())
    )
  );

  immediateSyncCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncCart),
      withLatestFrom(this.store.select(selectIsAuthenticated)),
      tap(([_action, isAuthenticated]) => {
        if (isAuthenticated) {
          this.syncService.syncCartToBackend();
        }
      }),
      map(() => operationSuccess())
    )
  );

  persistCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addItem, removeItem, updateQuantity, updateProduct, loadCartSuccess),
      tap(() => {
        try {
          this.persistenceService.saveCartToLocalStorage();
        } catch (error) {
          this.errorHandler.handlePersistenceError(error, 'Persist Cart');
        }
      })
    ), { dispatch: false }
  );


  operationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(operationSuccess),
      tap(() => {
        this.store.dispatch(setLoading({ loading: false }));
      })
    ), { dispatch: false }
  );


  operationFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(operationFailure),
      tap(() => {
        this.store.dispatch(setLoading({ loading: false }));
      })
    ), { dispatch: false }
  );

  private loadFromBackend() {
    if (isCartHydrationBlocked() || shouldSkipCampaignCartHydration()) {
      return of({ items: [], source: 'backend' as const });
    }

    return this.store.select(selectUserUuid).pipe(
      switchMap(userUuid => {
        if (!userUuid) {
          throw new Error('User UUID not found');
        }
        
        return this.orderService.getOrderDraftByUuid(userUuid).pipe(
          map(draft => {
            if (draft?.orderItems?.length) {
              const cartItems = draft.orderItems.map(item => 
                createCartItem(item.product, item.quantity, item.selectedVariation)
              );
              return { items: cartItems, source: 'backend' as const };
            } else {
              return { items: [], source: 'backend' as const };
            }
          }),
          catchError(error => {
            console.error('Error loading cart from backend:', error);
            return of({ items: [], source: 'backend' as const });
          })
        );
      })
    );
  }

  private loadFromLocalStorage() {
    try {
      const items = this.persistenceService.loadCartFromLocalStorage();
      return of({ items, source: 'localStorage' as const });
    } catch (error) {
      this.errorHandler.handlePersistenceError(error, 'Load from LocalStorage');
      return of({ items: [], source: 'localStorage' as const });
    }
  }

  private syncToBackend() {
    return this.store.select(selectCartItems).pipe(
      switchMap(_items => {
        return of(true);
      })
    );
  }

  private syncToLocalStorage() {
    try {
      this.persistenceService.saveCartToLocalStorage();
      return of(true);
    } catch (error) {
      this.errorHandler.handlePersistenceError(error, 'Sync to LocalStorage');
      throw error;
    }
  }
}
